import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import { fetchHealth, type HealthReport } from '../../api/health';
import { Badge, Button, Card, ErrorState, LoadingState } from '../ui';

type Status = 'loading' | 'ready' | 'error';

/**
 * Whether the pieces this instance runs on are answering.
 *
 * It used to be on the public landing page, which is where Phase 28 found it:
 * a readiness report naming the database, the cache and the Celery broker, on
 * the brand's front door, to a visitor who has not signed in. It told them
 * nothing they could use and named infrastructure to somebody with no business
 * knowing it. This is where an operator already looks.
 */
export function AdminStatus() {
  const [status, setStatus] = useState<Status>('loading');
  const [report, setReport] = useState<HealthReport | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(() => {
    setStatus('loading');
    fetchHealth()
      .then((data) => {
        setReport(data);
        setStatus('ready');
      })
      .catch((caught: unknown) => {
        setError(caught instanceof ApiError ? caught : null);
        setStatus('error');
      });
  }, []);

  useEffect(load, [load]);

  return (
    <Card
      title="Platform status"
      description="Whether the pieces this instance runs on are answering."
      actions={
        <Button size="sm" variant="ghost" onClick={load}>
          Refresh
        </Button>
      }
    >
      {status === 'loading' && <LoadingState label="Checking the kitchen…" />}

      {status === 'error' && (
        <ErrorState
          description={error?.message ?? 'The API is not answering.'}
          requestId={error?.requestId}
          onRetry={load}
        />
      )}

      {status === 'ready' && report && (
        <ul className="sc-checks">
          {report.checks.map((check) => (
            <li key={check.name} className="sc-checks__item">
              <span className="sc-checks__name">{check.name.replace(/_/g, ' ')}</span>
              <Badge
                tone={check.status === 'ok' ? 'success' : 'danger'}
                srPrefix={`${check.name} status`}
              >
                {check.status === 'ok' ? '✅ Healthy' : '⚠️ Unavailable'}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
